import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Draggable from 'react-draggable';
import { useDocument } from '@nandorojo/swr-firestore';
import { useRecoilValue } from 'recoil';
import { SettingCollectionPath } from '@typings/collections.enum';
import {
  IPopupItem,
  IPopupSetting,
} from '@typings/setting/popupSetting.interface';
import Button from '@components/dataEntry/button/Button';
import dayjs from 'dayjs';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import Theme from '@styles/Theme';
import { popupReshowCounterState } from '@atom/setting/popup';
import { POPUP_DISMISS_STORAGE_PREFIX } from '@utils/popup/popupStorage';

/* ─── Storage helpers ─── */

function isDismissedToday(popupId: string): boolean {
  try {
    const raw = localStorage.getItem(
      `${POPUP_DISMISS_STORAGE_PREFIX}${popupId}`,
    );
    if (!raw) return false;
    return raw === dayjs().format('YYYY-MM-DD');
  } catch {
    return false;
  }
}

function dismissForToday(popupId: string) {
  try {
    localStorage.setItem(
      `${POPUP_DISMISS_STORAGE_PREFIX}${popupId}`,
      dayjs().format('YYYY-MM-DD'),
    );
  } catch { }
}

function isWithinPeriod(popup: IPopupItem): boolean {
  const today = dayjs().startOf('day');
  if (popup.startDate && dayjs(popup.startDate).isAfter(today)) return false;
  if (popup.endDate && dayjs(popup.endDate).endOf('day').isBefore(today))
    return false;
  return true;
}

/* ─── Constants ─── */

const POPUP_WIDTH = 420;

/* ─── Styles ─── */

/* Draggable의 직접 자식 — transform 위치를 react-draggable이 단독으로 제어 */
const PopupWrap = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 190;
  width: ${POPUP_WIDTH}px;
  max-width: calc(100vw - 32px);
  cursor: move;
`;

/* 시각적 카드 + 나타남/사라짐 애니메이션 — opacity만 담당 */
const PopupCard = styled(motion.div)`
  background: var(--backgroundColor);
  border-radius: var(--cardBorderRadius);
  border: 1px solid var(--lineColor);
  box-shadow: ${Theme.shadow.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;
  width: 100%;
  max-height: calc(100dvh - 100px);
`;

const PopupTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 14px 4px;
  flex-shrink: 0;
`;

const PreviewBadge = styled.div`
  width: 100%;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 5px 0;
  background: var(--primaryColor);
  color: #fff;
  flex-shrink: 0;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PaginationInfo = styled.span`
  font-size: 12px;
  color: var(--subTextColor);
  min-width: 36px;
  text-align: center;
`;

const PopupBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  max-height: 500px;
  padding: 12px 20px 16px;

  @media (max-width: 640px) {
    max-height: none;
  }
  ${scrollbarStyle}

  img {
    max-width: 100%;
    height: auto;
  }
  line-height: 1.8;
  font-size: 14px;
  color: var(--textColor);
  cursor: default; /* 본문은 텍스트 커서 */
`;

const PopupFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px 14px;
  border-top: 1px solid var(--lineColor);
  flex-shrink: 0;
  background: var(--cardBackgroundColor);
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: var(--subTextColor);
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;
  white-space: nowrap;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--textColor);
  }
`;

interface Props {
  /** 미리보기 모드: 이 값이 있으면 Firestore 무시하고 해당 팝업 하나만 표시 */
  previewItem?: IPopupItem;
  /** 미리보기 모드에서 닫기 콜백 */
  onPreviewClose?: () => void;
}

/* ─── Component ─── */

function AnnouncementPopup({ previewItem, onPreviewClose }: Props) {
  const isPreviewMode = !!previewItem;

  const { data: popupSetting } = useDocument<IPopupSetting>(
    isPreviewMode ? null : SettingCollectionPath.POPUP,
    { listen: false },
  );

  const [closedIds, setClosedIds] = useState<Set<string>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const nodeRef = useRef<HTMLDivElement>(null);

  // [팝업 공지 보기] 트리거: 카운터 변경 시 dismiss 해제된 공지를 다시 보여준다.
  const reshowCounter = useRecoilValue(popupReshowCounterState);
  useEffect(() => {
    if (isPreviewMode) return;
    if (reshowCounter === 0) return;
    setClosedIds(new Set());
    setCurrentIndex(0);
  }, [reshowCounter, isPreviewMode]);

  // 초기 위치: 화면 상단 중앙
  const initialX = useMemo(
    () =>
      typeof window !== 'undefined'
        ? Math.max(16, (window.innerWidth - POPUP_WIDTH) / 2)
        : 100,
    [],
  );
  const initialY = 80;

  const visiblePopups = useMemo(() => {
    // 미리보기 모드: 해당 아이템 하나만
    if (isPreviewMode && previewItem) return [previewItem];
    if (!popupSetting?.popups) return [];
    return popupSetting.popups.filter(
      (p) =>
        p.isActive &&
        isWithinPeriod(p) &&
        !isDismissedToday(p.id) &&
        !closedIds.has(p.id),
    );
  }, [isPreviewMode, previewItem, popupSetting, closedIds]);

  useEffect(() => {
    if (currentIndex >= visiblePopups.length && visiblePopups.length > 0) {
      setCurrentIndex(visiblePopups.length - 1);
    }
  }, [visiblePopups.length, currentIndex]);

  // previewItem이 바뀌면 인덱스 초기화
  useEffect(() => {
    setCurrentIndex(0);
  }, [previewItem?.id]);

  const currentPopup = visiblePopups[currentIndex];

  const handleClose = useCallback(() => {
    if (isPreviewMode) {
      onPreviewClose?.();
      return;
    }
    // [닫기]: 현재 표시 중인 모든 팝업을 한 번에 닫는다 (개수 무관).
    if (visiblePopups.length === 0) return;
    setClosedIds((prev) => {
      const next = new Set(prev);
      visiblePopups.forEach((p) => next.add(p.id));
      return next;
    });
  }, [isPreviewMode, onPreviewClose, visiblePopups]);

  const handleDismissToday = useCallback(() => {
    if (isPreviewMode) {
      onPreviewClose?.();
      return;
    }
    if (!currentPopup) return;
    dismissForToday(currentPopup.id);
    setClosedIds((prev) => new Set(prev).add(currentPopup.id));
  }, [isPreviewMode, onPreviewClose, currentPopup]);

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((i) => Math.max(0, i - 1));
    },
    [],
  );

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((i) => Math.min(visiblePopups.length - 1, i + 1));
    },
    [visiblePopups.length],
  );

  if (visiblePopups.length === 0 || !currentPopup) return null;

  return (
    <Draggable
      nodeRef={nodeRef}
      defaultPosition={{ x: initialX, y: initialY }}
      bounds="body"
      cancel="button, a, .popup-body"
    >
      {/* PopupWrap: react-draggable이 transform으로 위치만 제어 */}
      <PopupWrap ref={nodeRef}>
        {/* PopupCard: opacity 애니메이션만 담당 — transform 건드리지 않음 */}
        <PopupCard
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* 미리보기 배지 */}
          {isPreviewMode && <PreviewBadge>미리보기</PreviewBadge>}

          {/* 페이지네이션 (팝업 여러 개일 때만) */}
          {visiblePopups.length > 1 && (
            <PopupTop>
              <PaginationRow>
                <Button
                  size="sm"
                  shape="stroke"
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                >
                  이전
                </Button>
                <PaginationInfo>
                  {currentIndex + 1} / {visiblePopups.length}
                </PaginationInfo>
                <Button
                  size="sm"
                  shape="stroke"
                  onClick={handleNext}
                  disabled={currentIndex === visiblePopups.length - 1}
                >
                  다음
                </Button>
              </PaginationRow>
            </PopupTop>
          )}

          {/* 본문 - cancel 클래스로 드래그 제외 */}
          <PopupBody
            className="popup-body"
            dangerouslySetInnerHTML={{ __html: currentPopup.content }}
          />

          {/* 푸터 */}
          <PopupFooter>
            <DismissButton onClick={handleDismissToday}>
              오늘 보지 않기
            </DismissButton>
            <Button size="sm" onClick={handleClose}>
              닫기
            </Button>
          </PopupFooter>
        </PopupCard>
      </PopupWrap>
    </Draggable>
  );
}

export default React.memo(AnnouncementPopup);
